import React from 'react';
import { SegmentedToggle } from 'components/SegmentedToggle';
import { CardFinish, FINISH_LABELS, FINISH_ORDER } from 'utils/finishes';
import './FinishSegmentedControl.css';

interface FinishSegmentedControlProps {
  value: CardFinish;
  /** Finishes the printing exists in (from getAvailableFinishes). Others render disabled, never hidden (D-3). */
  available: readonly CardFinish[];
  onChange: (finish: CardFinish) => void;
  /** Whole-control disable (saving, loading) */
  disabled?: boolean;
  /** Short labels (NF / F / E) for table rows */
  compact?: boolean;
  ariaLabel?: string;
  className?: string;
  /**
   * Caption under the control naming the finishes this printing does not
   * exist in ("This printing isn't available in non-foil."), design 1b i.
   */
  showCaption?: boolean;
}

const COMPACT_LABELS: Record<CardFinish, string> = { nonfoil: 'NF', foil: 'F', etched: 'E' };

/**
 * Single-select finish control for a HAVE row (Design Pass D-3): a card in hand
 * is exactly one finish. Every finish is always shown in canonical order so the
 * card's finish space stays legible; a finish the printing does not exist in is
 * disabled with a title saying so, never hidden.
 */
export const FinishSegmentedControl: React.FC<FinishSegmentedControlProps> = ({
  value,
  available,
  onChange,
  disabled = false,
  compact = false,
  ariaLabel = 'Finish',
  className = '',
  showCaption = false,
}) => {
  const missing = FINISH_ORDER.filter((f) => !available.includes(f)).map((f) => FINISH_LABELS[f].toLowerCase());
  const caption =
    showCaption && missing.length > 0
      ? `This printing isn't available in ${missing.length === 1 ? missing[0] : `${missing.slice(0, -1).join(', ')} or ${missing[missing.length - 1]}`}.`
      : null;
  const control = (
  <SegmentedToggle<CardFinish>
    className={`finish-segmented segmented-toggle--calm ${compact ? 'finish-segmented--compact' : ''} ${className}`}
    ariaLabel={ariaLabel}
    value={value}
    onChange={onChange}
    disabled={disabled}
    options={FINISH_ORDER.map((finish) => {
      const exists = available.includes(finish);
      return {
        value: finish,
        label: compact ? COMPACT_LABELS[finish] : FINISH_LABELS[finish],
        disabled: !exists,
        title: exists
          ? FINISH_LABELS[finish]
          : `${FINISH_LABELS[finish]}: this printing does not exist in this finish`,
      };
    })}
  />
  );
  if (!caption) return control;
  return (
    <div className="finish-segmented-block">
      {control}
      <p className="finish-segmented__caption" data-testid="finish-caption">
        {caption}
      </p>
    </div>
  );
};

export default FinishSegmentedControl;
